import { z } from 'zod';

export const AccountType = z.enum([
  'CURRENT',
  'SAVINGS',
  'CREDIT_CARD',
  'CASH',
  'INVESTMENT',
  'LOAN',
  'MORTGAGE',
  'ASSET',
  'LIABILITY',
]);
export const Visibility = z.enum(['SHARED', 'SUMMARY_ONLY', 'PRIVATE']);
export const ExpenseType = z.enum(['PERSONAL', 'SHARED', 'REIMBURSABLE', 'EXCLUDED']);
export const SplitMethod = z.enum(['EQUAL', 'INCOME_PROPORTIONAL', 'PERCENTAGES', 'EXACT', 'SHARES']);
export const TransactionKind = z.enum([
  'EXPENSE',
  'INCOME',
  'TRANSFER',
  'REFUND',
  'SETTLEMENT_PAYMENT',
  'BALANCE_ADJUSTMENT',
]);
export const TransactionStatus = z.enum(['PENDING', 'BOOKED', 'REVERSED', 'CANCELLED']);
export const TransactionSource = z.enum(['BANK', 'MANUAL', 'CSV', 'OFX', 'QFX', 'CAMT', 'SYSTEM']);
export const CategoryKind = z.enum(['EXPENSE', 'INCOME', 'TRANSFER']);
export const RuleScope = z.enum(['HOUSEHOLD', 'CATEGORY', 'ACCOUNT']);
export const SettlementStatus = z.enum(['SETTLED', 'UNDONE']);
export const AccountProvider = z.enum(['MANUAL', 'FAKE_BANK', 'TRUELAYER', 'LUNCHFLOW', 'PLAID', 'TRADING212', 'FAKE_INVEST']);
export const ConnectionStatus = z.enum([
  'CONNECTED',
  'SYNCING',
  'ACTION_REQUIRED',
  'CONSENT_EXPIRING',
  /** The bank's consent ended: the owner confirms access again (history stays). */
  'CONSENT_EXPIRED',
  'EXPIRED',
  /** The bank refused more reads for now; the next try is at `nextRefreshAt`. */
  'RATE_LIMITED',
  'ERROR',
  'DISCONNECTED',
  'UNSUPPORTED',
]);
export const InvestmentEventType = z.enum([
  'DEPOSIT',
  'WITHDRAWAL',
  'TRANSFER',
  'DIVIDEND',
  'INTEREST',
  'FEE',
  'TAX',
  'BUY',
  'SELL',
  'OTHER',
]);
export const RecurringCadence = z.enum(['WEEKLY', 'FORTNIGHTLY', 'FOUR_WEEKLY', 'MONTHLY', 'QUARTERLY', 'YEARLY']);
export const RecurringKind = z.enum(['SUBSCRIPTION', 'BILL', 'DEBT', 'INCOME', 'TRANSFER', 'OTHER']);
export const RecurringStatus = z.enum(['SUGGESTED', 'CONFIRMED', 'DISMISSED']);

/** Entity types in the sync protocol. */
export const EntityType = z.enum([
  'household',
  'person',
  'account',
  'categoryGroup',
  'category',
  'tag',
  'transaction',
  'incomePeriod',
  'settlementRule',
  'settlement',
  'recurringPattern',
  'investmentConnection',
  'investmentPortfolio',
  'investmentEvent',
  'bankConnection',
  'budget',
  'savingsGoal',
]);
export type EntityType = z.infer<typeof EntityType>;

/** Entity types clients may write. The rest are written only by the backend (integrations). */
export const CLIENT_WRITABLE: ReadonlySet<EntityType> = new Set<EntityType>([
  'household',
  'person',
  'account',
  'categoryGroup',
  'category',
  'tag',
  'transaction',
  'incomePeriod',
  'settlementRule',
  'settlement',
  'recurringPattern',
  'budget',
  'savingsGoal',
]);

/** Order used by full resync and seeding: referenced entities before those referencing them. */
export const ENTITY_ORDER: readonly EntityType[] = [
  'household',
  'person',
  'categoryGroup',
  'category',
  'tag',
  'bankConnection',
  'investmentConnection',
  'account',
  'incomePeriod',
  'settlementRule',
  'transaction',
  'settlement',
  'recurringPattern',
  'budget',
  'savingsGoal',
  'investmentPortfolio',
  'investmentEvent',
];
